<template>
  <div class="report">
    <Watermark v-if="isTestEnv" />
    <ReportCoverPage
      v-if="!!includeCoverPage"
      :sizing="sizing"
      :dateGenerated="dateGenerated"
    />

    <table class="report-print-table">
      <tbody>
        <tr>
          <td>
            <div class="content">
              <section
                v-if="reportSections.designSchematic.enabledScenarios.size"
                class="break-inside-avoid"
              >
                <template
                  v-for="scenarioId in [
                    ...reportSections.designSchematic.enabledScenarios
                  ]"
                  :key="`schematic-${scenarioId}`"
                >
                  <ReportSectionHeader
                    section-title="Design Schematic"
                    :customer-name="sizing?.customer?.customer_name"
                    :sizing-name="sizing?.title"
                    :well-name="headerWellNameOrLocation"
                    :remove-page-break="!includeCoverPage"
                    :date-generated="dateGenerated"
                  />
                  <ScenarioEquipmentSchematic
                    :scenario="scenarioMap[scenarioId].scenario"
                    :scenario-calculations="scenarioMap[scenarioId].calcs"
                    :sagd-mode="reportSections.designSchematic.sagd"
                    class="schematic"
                  />
                </template>
              </section>

              <section
                v-if="reportSections.designOverview.enabledScenarios.size"
              >
                <DesignOverview
                  :sizing="sizing"
                  :remove-page-break="!includeCoverPage"
                  :scenarios="
                    Array.from(reportSections.designOverview.enabledScenarios)
                      .map(id => {
                        return scenarioMap[id];
                      })
                      .filter(s => !!s)
                  "
                />
                <RecommendationProvenance
                  v-for="scenarioId in reportSections.designOverview.enabledScenarios"
                  :key="`recommendation-${scenarioId}`"
                  :scenario="scenarioMap[scenarioId].scenario"
                />
              </section>

              <section
                v-if="reportSections.multiFrequencyHead.enabledScenarios.size"
              >
                <ReportSectionHeader
                  section-title="Multi-Frequency Head Curve"
                  :customer-name="sizing?.customer?.customer_name"
                  :sizing-name="sizing?.title"
                  :well-name="headerWellNameOrLocation"
                  :date-generated="dateGenerated"
                />
                <div
                  v-for="scenarioId in [
                    ...reportSections.multiFrequencyHead.enabledScenarios
                  ]"
                  :key="`mfh-${scenarioId}`"
                  class="always-break-after"
                >
                  <MultiFrequencyTDHReportSet
                    v-if="scenarioMap[scenarioId].scenario"
                    :chart-min-height="30"
                    :min-frequency="frequencyRange[0]"
                    :max-frequency="frequencyRange[1]"
                    :scenario="scenarioMap[scenarioId].scenario"
                    :scenario-calculations="scenarioMap[scenarioId].calcs"
                  ></MultiFrequencyTDHReportSet>
                </div>
              </section>

              <section
                v-if="reportSections.multiFrequencyBHP.enabledScenarios.size"
              >
                <ReportSectionHeader
                  section-title="Multi-Frequency BHP Curve"
                  :customer-name="sizing?.customer?.customer_name"
                  :sizing-name="sizing?.title"
                  :well-name="headerWellNameOrLocation"
                  :date-generated="dateGenerated"
                />
                <div
                  v-for="scenarioId in [
                    ...reportSections.multiFrequencyBHP.enabledScenarios
                  ]"
                  :key="`mfb-${scenarioId}`"
                  class="always-break-after"
                >
                  <MultiFrequencyBHPReportSet
                    :chart-min-height="30"
                    :min-frequency="frequencyRange[0]"
                    :max-frequency="frequencyRange[1]"
                    :scenario="scenarioMap[scenarioId].scenario"
                    :scenario-calculations="scenarioMap[scenarioId].calcs"
                  ></MultiFrequencyBHPReportSet>
                </div>
              </section>

              <section
                v-if="reportSections.individualPumpCurve.enabledScenarios.size"
              >
                <ReportSectionHeader
                  section-title="Single Pump Charts"
                  :customer-name="sizing?.customer?.customer_name"
                  :sizing-name="sizing?.title"
                  :well-name="headerWellNameOrLocation"
                  :date-generated="dateGenerated"
                />
                <div
                  v-for="scenarioId in [
                    ...reportSections.individualPumpCurve.enabledScenarios
                  ]"
                  :key="`spc-${scenarioId}`"
                  class="break-inside-avoid"
                >
                  <SinglePumpChart
                    class="break-inside-avoid"
                    is-horizontal
                    is-static
                    :aspect-ratio="aspectRatio"
                    :title="scenarioMap[scenarioId].scenario?.title || ''"
                    :scenario="scenarioMap[scenarioId].scenario"
                    :scenario-calculations="scenarioMap[scenarioId].calcs"
                    :draw-legend="true"
                    :display-npsh-toggle="false"
                    hide-pump-selector
                  />
                  <br />
                  <PumpCalcTable
                    :sizing="sizing"
                    :scenario="scenarioMap[scenarioId].scenario"
                    :scenario-calculations="scenarioMap[scenarioId].calcs"
                    :is-report="true"
                  />
                </div>
              </section>

              <section
                v-if="
                  reportSections.individualPumpNPSHCurve.enabledScenarios.size
                "
              >
                <ReportSectionHeader
                  section-title="Single Pump NPSH Chart"
                  :customer-name="sizing?.customer?.customer_name"
                  :sizing-name="sizing?.title"
                  :well-name="headerWellNameOrLocation"
                  :date-generated="dateGenerated"
                />
                <div
                  v-for="scenarioId in [
                    ...reportSections.individualPumpNPSHCurve.enabledScenarios
                  ]"
                  :key="`spc-${scenarioId}`"
                  class="break-inside-avoid"
                >
                  <SinglePumpNPSHChart
                    class="break-inside-avoid"
                    is-horizontal
                    is-static
                    :aspect-ratio="aspectRatio"
                    :title="scenarioMap[scenarioId].scenario?.title || ''"
                    :scenario="scenarioMap[scenarioId].scenario"
                    :scenario-calculations="scenarioMap[scenarioId].calcs"
                    :draw-legend="true"
                  />
                </div>
              </section>
              <section
                v-if="reportSections.multiFrequency?.enabledScenarios.size"
              >
                <ReportSectionHeader
                  section-title="Multifrequency"
                  :customer-name="sizing?.customer?.customer_name"
                  :sizing-name="sizing?.title"
                  :well-name="headerWellNameOrLocation"
                  :date-generated="dateGenerated"
                />
                <div
                  v-for="scenarioId in [
                    ...reportSections.multiFrequency.enabledScenarios
                  ]"
                  :key="`mfc-${scenarioId}`"
                  class="always-break-after"
                >
                  <MultiFrequencyCombined
                    :chart-min-height="30"
                    :min-frequency="frequencyRange[0]"
                    :max-frequency="frequencyRange[1]"
                    :scenario="scenarioMap[scenarioId].scenario"
                    :scenario-calculations="scenarioMap[scenarioId].calcs"
                  />
                </div>
              </section>
              <section
                v-if="
                  reportSections.multiscenarioPumpCurve.enabledScenarios.size
                "
                class="break-inside-avoid"
              >
                <ReportSectionHeader
                  section-title="Multiscenario Pump Curve"
                  :customer-name="sizing?.customer?.customer_name"
                  :sizing-name="sizing?.title"
                  :well-name="headerWellNameOrLocation"
                  :date-generated="dateGenerated"
                />
                <MultiscenarioPumpChart
                  :aspect-ratio="aspectRatio"
                  draw-legend
                  is-horizontal
                  is-static
                  :sizing="sizing"
                  :scenarios="
                    Array.from(
                      reportSections.multiscenarioPumpCurve.enabledScenarios
                    )
                      .map(scenarioId => {
                        return scenarioMap[scenarioId];
                      })
                      .filter(s => !!s)
                  "
                />
              </section>

              <section
                v-if="reportSections.multiFrequencyPump.enabledScenarios.size"
              >
                <ReportSectionHeader
                  section-title="Multifrequency Tapered Pumps"
                  :customer-name="sizing?.customer?.customer_name"
                  :sizing-name="sizing?.title"
                  :well-name="headerWellNameOrLocation"
                  :date-generated="dateGenerated"
                />
                <div
                  v-for="(scenarioId, i) in [
                    ...reportSections.multiFrequencyPump.enabledScenarios
                  ]"
                  :key="`mfp-${scenarioId}`"
                  :class="{ 'break-inside-avoid': i > 0 }"
                >
                  <h3>{{ scenarioMap[scenarioId].scenario?.title }}</h3>
                  <TaperedSinglePumpTDHChart
                    v-for="(xrefPump, pIdx) in scenarioMap[scenarioId].calcs
                      .descendingOrderPumps.value"
                    :key="`mfp-${xrefPump.id}`"
                    :class="{ 'break-inside-avoid': pIdx > 0 }"
                    draw-legend
                    is-horizontal
                    is-static
                    :xref-pump="xrefPump"
                    :minimum-height-px="370"
                    :scenario="scenarioMap[scenarioId].scenario"
                    :show-operating-frequency="false"
                    :min-frequency="frequencyRange[0]"
                    :max-frequency="frequencyRange[1]"
                    :scenario-calculations="scenarioMap[scenarioId].calcs"
                    x-legend-label="In Situ Flow Rate"
                    y-legend-label="TDH"
                  />
                </div>
              </section>

              <section
                v-if="reportSections.pumpSpeedTorque.enabledScenarios.size"
              >
                <ReportSectionHeader
                  section-title="Speed - Torque"
                  :customer-name="sizing?.customer?.customer_name"
                  :sizing-name="sizing?.title"
                  :well-name="headerWellNameOrLocation"
                  :date-generated="dateGenerated"
                />
                <div
                  v-for="scenarioId in [
                    ...reportSections.pumpSpeedTorque.enabledScenarios
                  ]"
                  :key="`pst-${scenarioId}`"
                  class="break-inside-avoid"
                >
                  <PumpSpeedTorque
                    class="break-inside-avoid"
                    is-horizontal
                    is-static
                    :aspect-ratio="aspectRatio"
                    :title="scenarioMap[scenarioId].scenario?.title || ''"
                    :scenario-calculations="scenarioMap[scenarioId].calcs"
                    :draw-legend="true"
                  />
                </div>
              </section>

              <section
                v-if="reportSections.motorPerformance.enabledScenarios.size"
              >
                <ReportSectionHeader
                  section-title="Motor Performance"
                  :customer-name="sizing?.customer?.customer_name"
                  :sizing-name="sizing?.title"
                  :well-name="headerWellNameOrLocation"
                  :date-generated="dateGenerated"
                />
                <MotorPerformanceChart
                  v-for="scenarioId in [
                    ...reportSections.motorPerformance.enabledScenarios
                  ]"
                  :key="`mp-${scenarioId}`"
                  class="break-inside-avoid"
                  :aspect-ratio="aspectRatio"
                  draw-legend
                  draw-motor-details
                  is-static
                  is-horizontal
                  :title="scenarioMap[scenarioId].scenario?.title || ''"
                  :equipment="scenarioMap[scenarioId].equipment"
                  :parameters="scenarioMap[scenarioId].parameters"
                  :scenario-calculations="scenarioMap[scenarioId].calcs"
                />
              </section>

              <section v-if="reportSections.gasCurve.enabledScenarios.size">
                <ReportSectionHeader
                  section-title="Gas Curve"
                  :customer-name="sizing?.customer?.customer_name"
                  :sizing-name="sizing?.title"
                  :well-name="headerWellNameOrLocation"
                  :date-generated="dateGenerated"
                />
                <GasCurveChart
                  v-for="scenarioId in [
                    ...reportSections.gasCurve.enabledScenarios
                  ]"
                  :key="`gc-${scenarioId}`"
                  class="break-inside-avoid"
                  :aspect-ratio="aspectRatio"
                  draw-legend
                  is-static
                  is-horizontal
                  :display-pump-thresholds="
                    reportSections.gasCurve.showPumpThresholds
                  "
                  :title="scenarioMap[scenarioId].scenario?.title || ''"
                  :scenario-calculations="scenarioMap[scenarioId].calcs"
                  :correlations="scenarioMap[scenarioId].correlations"
                  hide-threshold-control
                />
              </section>

              <section
                v-if="reportSections.cableTemperature.enabledScenarios.size"
              >
                <ReportSectionHeader
                  section-title="Cable Temperature"
                  :customer-name="sizing?.customer?.customer_name"
                  :sizing-name="sizing?.title"
                  :well-name="headerWellNameOrLocation"
                  :date-generated="dateGenerated"
                />
                <CableTemperatureChart
                  v-for="scenarioId in [
                    ...reportSections.cableTemperature.enabledScenarios
                  ]"
                  :key="`ct-${scenarioId}`"
                  class="break-inside-avoid"
                  :aspect-ratio="aspectRatio"
                  draw-legend
                  is-horizontal
                  is-static
                  :title="scenarioMap[scenarioId].scenario?.title || ''"
                  :scenario="scenarioMap[scenarioId].scenario"
                  :scenario-calculations="scenarioMap[scenarioId].calcs"
                  :equipment="scenarioMap[scenarioId].equipment"
                  :cables="scenarioMap[scenarioId].cables"
                />
              </section>

              <section v-if="reportSections.crossSection.enabledScenarios.size">
                <ReportSectionHeader
                  section-title="Cross Section Analysis"
                  :customer-name="sizing?.customer?.customer_name"
                  :sizing-name="sizing?.title"
                  :well-name="headerWellNameOrLocation"
                  :date-generated="dateGenerated"
                />
                <CrossSectionChartsSet
                  v-for="(scenarioId, idx) in [
                    ...reportSections.crossSection.enabledScenarios
                  ]"
                  :key="`cs-${scenarioId}`"
                  :class="{ 'break-inside-avoid': idx > 0 }"
                  :title="scenarioMap[scenarioId].scenario?.title || ''"
                  :scenario-calculations="scenarioMap[scenarioId].calcs"
                  :scenario="scenarioMap[scenarioId].scenario"
                />
              </section>

              <section
                v-if="reportSections.pressureTraverse.enabledScenarios.size"
              >
                <ReportSectionHeader
                  section-title="Pressure Traverse"
                  :customer-name="sizing?.customer?.customer_name"
                  :sizing-name="sizing?.title"
                  :well-name="headerWellNameOrLocation"
                  :remove-page-break="
                    !reportSections.IPRGraph.enabledScenarios.size
                  "
                  :date-generated="dateGenerated"
                />
                <PressureTraverseChart
                  v-for="scenarioId in [
                    ...reportSections.pressureTraverse.enabledScenarios
                  ]"
                  :key="`pt-${scenarioId}`"
                  class="break-inside-avoid"
                  :aspect-ratio="aspectRatio"
                  is-static
                  :title="scenarioMap[scenarioId].scenario?.title || ''"
                  :scenario="scenarioMap[scenarioId].scenario"
                  :scenario-calculations="scenarioMap[scenarioId].calcs"
                  :well-properties="scenarioMap[scenarioId].wellProperties"
                />
              </section>

              <section
                v-if="reportSections.IPRGraph.enabledScenarios.size"
                class="break-inside-avoid"
              >
                <ReportSectionHeader
                  section-title="IPR"
                  :customer-name="sizing?.customer?.customer_name"
                  :sizing-name="sizing?.title"
                  :well-name="headerWellNameOrLocation"
                  :date-generated="dateGenerated"
                />
                <IPRChart
                  v-for="scenarioId in [
                    ...reportSections.IPRGraph.enabledScenarios
                  ]"
                  :key="`ipr-${scenarioId}`"
                  :aspect-ratio="aspectRatio"
                  draw-legend
                  is-static
                  is-horizontal
                  :title="scenarioMap[scenarioId].scenario?.title || ''"
                  :scenario="scenarioMap[scenarioId].scenario"
                  :scenario-calculations="scenarioMap[scenarioId].calcs"
                  :well-properties="scenarioMap[scenarioId].wellProperties"
                />
              </section>

              <section
                v-if="
                  [
                    ...reportSections.deviationSurveyDepth.enabledScenarios
                  ].filter(
                    scenarioId => scenarioMap[scenarioId].deviationSurvey
                  ).length > 0
                "
              >
                <ReportSectionHeader
                  section-title="Deviation Surveys"
                  :customer-name="sizing?.customer?.customer_name"
                  :sizing-name="sizing?.title"
                  :well-name="headerWellNameOrLocation"
                  :date-generated="dateGenerated"
                />
                <div
                  v-for="scenarioId in [
                    ...reportSections.deviationSurveyDepth.enabledScenarios
                  ].filter(
                    scenarioId => scenarioMap[scenarioId].deviationSurvey
                  )"
                  :key="`dsd-${scenarioId}`"
                  class="break-inside-avoid"
                >
                  <DeviationSurveyDepth
                    :title="scenarioMap[scenarioId].scenario?.title || ''"
                    :entry-rows="
                      scenarioMap[scenarioId].calcs.entryRows.value.data
                    "
                    :scenario="scenarioMap[scenarioId].scenario"
                    :scenario-calculations="scenarioMap[scenarioId].calcs"
                    :aspect-ratio="aspectRatio"
                    :survey-type="
                      scenarioMap[scenarioId].calcs.entryRows.value.surveyType
                    "
                    is-static
                  />

                  <DeviationSurveyTop
                    v-if="
                      scenarioMap[scenarioId].deviationSurvey
                        ?.directional_data_set ===
                      Deviation_Survey_Directional_Dataset_Enum.ThreeDMdInclinationAzimuth
                    "
                    :title="
                      scenarioMap[scenarioId].scenario?.title ||
                      '' + ' Azimuth Visualization' ||
                      ''
                    "
                    :survey-type="
                      scenarioMap[scenarioId].calcs.entryRows.value.surveyType
                    "
                    is-static
                    :scenario="scenarioMap[scenarioId].scenario"
                    :scenario-calculations="scenarioMap[scenarioId].calcs"
                    :entry-rows="
                      scenarioMap[scenarioId].calcs.entryRows.value.data
                    "
                    :aspect-ratio="aspectRatio"
                  />
                </div>
              </section>

              <section
                v-if="
                  [...reportSections.bendingStressAnalysis.enabledScenarios]
                    .length > 0
                "
              >
                <ReportSectionHeader
                  section-title="Bending Stress Analysis"
                  :customer-name="sizing?.customer?.customer_name"
                  :sizing-name="sizing?.title"
                  :well-name="headerWellNameOrLocation"
                  :date-generated="dateGenerated"
                />
                <div
                  v-for="{ scenarioId, placement } in bendingStressCharts"
                  :key="`bending-${scenarioId}-${placement}`"
                  class="break-inside-avoid"
                >
                  <DeviationSurveyBendingStressAnalysis
                    is-static
                    :disable-tracking="true"
                    disable-equipment-legend
                    :title="scenarioMap[scenarioId].scenario?.title || ''"
                    :placement="placement"
                    :scenario="scenarioMap[scenarioId].scenario"
                    :scenario-calculations="scenarioMap[scenarioId].calcs"
                    :entry-rows="
                      scenarioCanDisplayBendingStress(scenarioId)
                        ? scenarioMap[scenarioId].calcs.entryRows.value.data
                        : []
                    "
                    :draw-legend="true"
                    :draw-equipment-labels="false"
                    enable-mouse-tracking
                    :aspect-ratio="aspectRatio"
                  />
                </div>
              </section>
              <section
                v-if="reportSections.PVTCorrelationList.enabledScenarios.size"
              >
                <ReportSectionHeader
                  section-title="Correlations List"
                  :customer-name="sizing?.customer?.customer_name"
                  :sizing-name="sizing?.title"
                  :well-name="headerWellNameOrLocation"
                  :date-generated="dateGenerated"
                />
                <CorrelationsList
                  :scenarios="
                    Array.from(
                      reportSections.PVTCorrelationList.enabledScenarios
                    )
                      .map(scenarioId => {
                        return scenarioMap[scenarioId];
                      })
                      .filter(s => !!s)
                  "
                />
              </section>
              <section v-if="reportSections.vsdResults.enabledScenarios.size">
                <ReportSectionHeader
                  section-title="VSD"
                  :customer-name="sizing?.customer?.customer_name"
                  :sizing-name="sizing?.title"
                  :well-name="headerWellNameOrLocation"
                  :date-generated="dateGenerated"
                />
                <VSDReport
                  v-for="scenarioId in [
                    ...reportSections.vsdResults.enabledScenarios
                  ]"
                  :key="`vsd-${scenarioId}`"
                  :scenario-calculations="scenarioMap[scenarioId].calcs"
                  :scenario="scenarioMap[scenarioId].scenario"
                />
              </section>
            </div>
          </td>
        </tr>
      </tbody>
      <tfoot>
        <tr>
          <td>
            <ReportFooter />
          </td>
        </tr>
      </tfoot>
    </table>
  </div>
</template>

<script setup lang="ts">
import type { Sizing } from "@/composables/forms/formTypes";
import type { ScenarioMap, SizingReportSections } from "@/components/types";

import { computed, ref, toRefs, watch } from "vue";
import {
  Deviation_Survey_Directional_Dataset_Enum,
  Sizing_Pumping_Type_Enum
} from "gql/api";
import DeviationSurveyDepth from "@/components/scenarios/charts/DeviationSurveyDepth.vue";
import SinglePumpChart from "@/components/scenarios/charts/SinglePumpChart.vue";
import SinglePumpNPSHChart from "@/components/scenarios/charts/SinglePumpNPSHChart.vue";
import MotorPerformanceChart from "@/components/scenarios/charts/MotorPerformanceChart.vue";
import CableTemperatureChart from "@/components/scenarios/charts/CableTemperatureChart.vue";
import PressureTraverseChart from "@/components/scenarios/charts/PressureTraverseChart.vue";
import GasCurveChart from "@/components/scenarios/charts/GasCurveChart.vue";
import PumpCalcTable from "@/components/scenarios/PumpCalcTable.vue";
import MultiscenarioPumpChart from "@/components/scenarios/charts/MultiscenarioPumpChart.vue";
import IPRChart from "@/components/scenarios/charts/IPRChart.vue";
import TaperedSinglePumpTDHChart from "@/components/scenarios/charts/TaperedSinglePumpTDHChart.vue";
import DeviationSurveyTop from "@/components/scenarios/charts/DeviationSurveyTop.vue";
import DeviationSurveyBendingStressAnalysis from "@/components/scenarios/charts/DeviationSurveyBendingStressAnalysis.vue";
import DesignOverview from "@/components/scenarios/DesignOverview.vue";
import RecommendationProvenance from "@/components/reports/RecommendationProvenance.vue";
import MultiFrequencyBHPReportSet from "../scenarios/charts/MultiFrequencyBHPReportSet.vue";
import MultiFrequencyTDHReportSet from "../scenarios/charts/MultiFrequencyTDHReportSet.vue";
import MultiFrequencyCombined from "../scenarios/charts/MultiFrequencyCombined.vue";
import CorrelationsList from "@/components/scenarios/CorrelationsList.vue";
import VSDReport from "@/components/scenarios/charts/VSDReport.vue";
import ScenarioEquipmentSchematic from "@/components/schematic/ScenarioEquipmentSchematic.vue";
import ReportSectionHeader from "./ReportSectionHeader.vue";
import ReportCoverPage from "./ReportCoverPage.vue";
import { v4 } from "uuid";
import Watermark from "@/components/reports/Watermark.vue";
import ReportFooter from "@/components/reports/ReportFooter.vue";
import { ISPROD } from "@/libs/calculations/constants";
import CrossSectionChartsSet from "@/components/scenarios/charts/CrossSectionChartsSet.vue";
import PumpSpeedTorque from "@/components/scenarios/charts/PumpSpeedTorque.vue";
import { scenarioHasValidEquipmentBendingStressCalcs } from "@/libs/charting/deviationBendingStress/validation";
import { BENDING_STRESS_PLACEMENTS } from "@/libs/charting/deviationBendingStress/useDeviationBendingStressChartData";

const props = defineProps<{
  sizing: Sizing | null | undefined;
  frequencyRange: number[];
  scenarioMap: ScenarioMap;
  reportSections: SizingReportSections;
  includeCoverPage: boolean;
}>();

// One chart per placement. Each chart gets its own print block.
const bendingStressCharts = computed(() =>
  [...props.reportSections.bendingStressAnalysis.enabledScenarios].flatMap(
    scenarioId =>
      BENDING_STRESS_PLACEMENTS.map(placement => ({ scenarioId, placement }))
  )
);

const scenarioCanDisplayBendingStress = (scenarioId: string) => {
  const scenario = props.scenarioMap[scenarioId];
  if (!scenario) {
    return false;
  }
  const is3dSurvey =
    scenario?.deviationSurvey?.directional_data_set ===
    Deviation_Survey_Directional_Dataset_Enum.ThreeDMdInclinationAzimuth;
  if (
    !is3dSurvey &&
    (scenario?.wellProperties?.average_dls === null ||
      scenario?.wellProperties?.average_dls === undefined)
  ) {
    return false;
  }
  return scenarioHasValidEquipmentBendingStressCalcs(
    scenario.scenario,
    scenario.calcs
  );
};

const { frequencyRange } = toRefs(props);

const headerWellNameOrLocation = computed(() =>
  props.sizing?.pumping_type === Sizing_Pumping_Type_Enum.Hps
    ? props.sizing?.hps_location ?? null
    : props.sizing?.well?.well_name
);

const dateGenerated = Intl.DateTimeFormat("en-us").format(new Date());

const isTestEnv = computed(() => window.config.ENV !== ISPROD);

/**
 * Since the tornado charts are not reactive by default, we must setup
 * a watcher to re-render them when the frequencyRange is updated
 */
const headTornadoKey = ref(v4());
const bhpTornadoKey = ref(v4());

const refreshCharts = () => {
  headTornadoKey.value = v4();
  bhpTornadoKey.value = v4();
};

watch(frequencyRange, () => refreshCharts());

const aspectRatio = ref(2);
</script>

<style lang="scss" scoped>
// TODO: why is this here?
@import "./src/assets/scss/theme-spyglass/components/report_tables.scss";
// Shrink margin for printed report
// enough to squeeze out filename and junk
@page {
  margin: 30px;
}

// Plain table used only so <tfoot> repeats ReportFooter on every printed
// page natively (display: table-footer-group). Chromium does not reliably
// repaint position: fixed descendants across repeated print-preview
// cycles, especially combined with the visibility-toggle print isolation
// in SizingsReports.vue - but table header/footer groups are ordinary
// fragmented flow content and are redrawn per page like everything else.
.report-print-table {
  width: 100%;
  table-layout: fixed;
  border-collapse: collapse;

  td {
    padding: 0;
    border: none;
  }
}

.content {
  section {
    margin-top: 0px !important;
    &:first-child {
      break-before: avoid;
    }
    break-before: page;
  }
  > section {
    break-inside: avoid;
    page-break-inside: avoid;
  }
}

:deep(.ddv.custom-legend.horizontal) {
  padding: 0em 1em 0em 1em;
  .pi-check:before {
    content: initial;
  }
}
:deep(.cross-section-charts-wrapper) {
  h2.dashboard-title {
    margin: 8px;
    font-size: 18px;
  }
}
.content > section {
  break-before: page;
  margin: 0.25in 0;
  h2 {
    font-size: 12pt;
    font-weight: 500;
    background: var(--surface-100);
    color: var(--text-color);
    padding: 0.5em 0.25em;
  }
  .section {
    max-width: 8in;
    max-height: 10.5in;
  }
}

:deep(.break-inside-avoid) {
  break-inside: avoid;
}

:deep(.always-break-after) {
  page-break-after: always;
}

:deep(.always-break-after:last-child) {
  page-break-after: auto;
  break-after: auto;
}

// Only the final rendered report section can end the document. If its last
// chart block has an unconditional `page-break-after: always`, Chromium can
// produce a trailing footer-only page.
.content > section:last-of-type > .always-break-after:last-child {
  page-break-after: auto;
  break-after: auto;
}

@media print {
  :deep(.schematic) {
    display: block;
    width: 100%;
    height: auto;
    max-height: 8.8in;
    break-inside: avoid;
  }
  :deep(.ddv) {
    width: calc(100% - 2rem) !important;
    max-width: 100% !important;
    min-width: 0 !important;
    margin-right: 2rem;
  }
  :deep(svg) {
    max-width: 100%;
  }
  #cover-page {
    z-index: 1;
    background-color: white;
  }

  :deep(.report-table tr) {
    break-inside: avoid;
    page-break-inside: avoid;
  }
}

:deep(.cross-section-charts-wrapper) {
  .chart-cell {
    width: auto !important;
  }
}

:deep(.x-section-wrapper) {
  .ddv.scatter {
    flex: none;
    margin-left: -45px;
  }

  .custom-legend {
    padding: 20px 0 0 10px;
    .control {
      display: flex;
      width: 100%;

      div:first-of-type {
        height: 10px;
        width: 10px;
        display: inline-block;
        font-size: 11px;
        flex: 1;
        max-width: 10px;

        input {
          margin: 0;
          width: 10px;
          height: 10px;
          border-radius: 50%;
          vertical-align: top;
          appearance: none;
          -webkit-appearance: none;
          outline: none;
          cursor: pointer;
        }
      }

      label {
        flex: 1;
        display: inline-block;
        line-height: 14px;
        div:first-of-type {
          max-width: 100%;
          height: auto;
          width: 100%;
          display: block;
          flex: none;
          font-weight: bold;
        }
        .value {
          font-size: 11px !important;
        }
      }
    }
  }
}
</style>
